(function () {
  'use strict';

  const moneyAmount = (money) => {
    if (money == null) return NaN;
    return Number(money.amount ?? money);
  };

  const saleIcon = () => {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    const outline = document.createElementNS(namespace, 'path');
    const hole = document.createElementNS(namespace, 'path');

    icon.setAttribute('class', 'icon icon--price_tag');
    icon.setAttribute('width', '14');
    icon.setAttribute('height', '14');
    icon.setAttribute('viewBox', '0 0 16 16');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('fill', 'currentColor');
    outline.setAttribute('d', 'M7.59 1.34a1 1 0 0 1 .7-.29h5.66a1 1 0 0 1 1 1v5.66a1 1 0 0 1-.3.7L7.6 15.5a1 1 0 0 1-1.42 0L.52 9.83a1 1 0 0 1 0-1.42l7.07-7.07Zm6.36 6.37-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66Z');
    hole.setAttribute('d', 'M9.7 6.3a1 1 0 1 0 1.42-1.42A1 1 0 0 0 9.7 6.3ZM9 7a2 2 0 1 0 2.83-2.83A2 2 0 0 0 9 7Z');
    icon.append(outline, hole);
    return icon;
  };

  class RccProductBadges extends HTMLElement {
    static get graphqlRequirements() {
      const settings = window.MundizioCards?.badges || {};
      return {
        metafields: [{ namespace: 'theme', key: 'label' }],
        graphql: [
          'rccCreatedAt: createdAt\nrccAvailableForSale: availableForSale',
          'rccHighlight: metafield(namespace: "theme", key: "highlight") { type value }',
          ...(settings.newByTag ? ['rccTags: tags'] : []),
          ...(settings.newCollections?.length ? ['rccCollections: collections(first: 250) { nodes { handle } }'] : []),
        ],
      };
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    connectedCallback() {
      this.render();
    }

    render() {
      const product = this._product;
      const config = window.MundizioCards || {};
      const settings = config.badges || {};
      const labels = settings.labels || {};
      const variants = product?.variantsAll || (
        product?.graphql?.variants?.pageInfo?.hasNextPage === false
          ? product.graphql.variants.nodes : []
      ) || [];
      const badges = [];
      const customLabel = product?.metafields?.[0]?.value?.trim();
      const template = product?.templateSuffix || product?.template_suffix || '';
      const highlight = product?.graphql?.rccHighlight;
      if (highlight?.type === 'boolean' && highlight.value === 'true') this.setAttribute('data-highlight', 'true');
      else this.removeAttribute('data-highlight');

      if (settings.showCustom !== false && customLabel) badges.push({ type: 'custom', text: customLabel });

      // Storefront API does not expose template suffixes; only use explicit metadata when supplied.
      if (template.includes('preorder')) badges.push({ type: 'preorder', text: labels.preorder ?? 'Vorbestellung' });

      if (settings.showSoldOut !== false && product?.graphql?.rccAvailableForSale === false) {
        badges.push({ type: 'sold-out', text: labels.soldOut ?? 'Ausverkauft' });
      }

      let discount = 0;
      for (const variant of variants) {
        const price = moneyAmount(variant?.price);
        const compareAt = moneyAmount(variant?.compareAtPrice);
        if (Number.isFinite(price) && Number.isFinite(compareAt) && price > 0 && compareAt > price) {
          discount = Math.max(discount, settings.saleType === 'money'
            ? compareAt - price : Math.round((1 - price / compareAt) * 100));
        }
      }

      if (settings.showSale !== false && discount > 0) {
        let amount = discount;
        let text;
        if (settings.saleType === 'money') {
          const currency = variants.find((variant) => variant?.price?.currencyCode)?.price.currencyCode
            || config.currency || 'EUR';
          amount = new Intl.NumberFormat(config.locale || 'de-DE', { style: 'currency', currency })
            .format(discount).replace(/^(.+?)\s*€$/, '€$1');
          text = variants.length > 1 ? labels.valueUpTo ?? 'Bis zu {amount} Rabatt' : labels.value ?? 'Um {amount} reduziert';
        } else if (!settings.saleType || settings.saleType === 'percent') {
          text = variants.length > 1 ? labels.percentUpTo ?? 'Bis zu {amount}% Rabatt' : labels.percent ?? 'Um {amount}% reduziert';
        } else {
          text = labels.sale ?? 'Sale';
        }
        badges.push({ type: 'sale', text: text.replace(/\{\{\s*amount\s*\}\}|\{amount\}/g, String(amount)) });
      }

      const createdAt = Date.parse(product?.graphql?.rccCreatedAt || '');
      const age = Date.now() - createdAt;
      const newByDate = settings.newByDays !== false && Number.isFinite(createdAt)
        && age >= 0 && age < (settings.newDays ?? 30) * 86400000;
      const newByTag = settings.newByTag && settings.newTag && (product?.graphql?.rccTags || [])
        .some((tag) => tag.toLocaleLowerCase('de-DE') === settings.newTag.toLocaleLowerCase('de-DE'));
      const newByCollection = (product?.graphql?.rccCollections?.nodes || [])
        .some((collection) => settings.newCollections?.includes(collection.handle));
      if (settings.showNew !== false && (newByDate || newByTag || newByCollection)) {
        badges.push({ type: 'new', text: labels.new ?? 'Neuankömmling' });
      }

      const elements = badges.slice(0, 2).map(({ type, text }) => {
        const badge = document.createElement('span');
        badge.className = `product-label product-label--${type} rcc-product-badge`;
        if (type === 'sale' && (settings.saleIcon ?? 'price_tag') === 'price_tag') badge.append(saleIcon());
        badge.append(document.createTextNode(text));
        return badge;
      });

      this.replaceChildren(...elements);
      this.hidden = elements.length === 0;
    }
  }

  if (!customElements.get('rcc-product-badges')) {
    customElements.define('rcc-product-badges', RccProductBadges);
  }
})();
